import { Controller, Path, useFormContext } from 'react-hook-form';

import { LabeledInput, LabeledInputProps } from 'components/Blocks';

import { AnyFunction, AnyObject, StableOmit } from 'types';
import { translateError } from 'utils/validation';
import { useLang } from 'locale';

export interface FormInputProps<T extends AnyObject>
  extends StableOmit<LabeledInputProps, 'error'> {
  property: keyof T;
  showLoadingOnSubmit?: boolean;
  handleError?: boolean;
}

function mergeCallbacks<T extends AnyFunction>(
  main: T,
  ...otherFns: (AnyFunction | undefined)[]
): T {
  const merger: AnyFunction = (...params) => {
    main(...params);

    otherFns.forEach((caller) => caller?.(...params));
  };

  return merger as T;
}

export function FormInput<T extends AnyObject>({
  property,
  showLoadingOnSubmit,
  handleError = true,
  ...props
}: FormInputProps<T>) {
  const { t } = useLang();

  const { control } = useFormContext<T>();

  return (
    <Controller
      control={control}
      name={property as Path<T>}
      render={({ field: { onChange, onBlur, value }, formState }) => (
        <LabeledInput
          size="medium"
          value={value}
          loading={showLoadingOnSubmit && formState.isSubmitting}
          error={handleError && translateError(formState.errors[property], t)}
          autoCapitalize="none"
          autoCorrect="false"
          {...props}
          onValueChange={mergeCallbacks(onChange, props.onValueChange)}
          onBlur={mergeCallbacks(onBlur, props.onBlur)}
        />
      )}
    />
  );
}
